import type { Duration } from 'luxon'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import { DateTime } from 'luxon'
import type { ContainerType, TransactionDirection } from './services/apiClient'
import { AppointmentStatus } from './services/apiClient'

// To make mocking easier
export function reloadPage() {
  window.location.reload()
}

/**
 * Fast ISO 8601 date parsing using native Date constructor instead of Luxon's slow fromISO.
 * Returns a Luxon DateTime for compatibility with the rest of the codebase.
 *
 * @param isoString - ISO 8601 formatted string (e.g., "2025-03-27T14:30:00Z")
 * @param options - Optional Luxon DateTime options
 * @param options.zone - Optional timezone to use
 * @returns DateTime object
 */
const ISO_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const ISO_HAS_EXPLICIT_OFFSET_RE = /(?:z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i
export function fastParseISO(
  isoString: string,
  options?: { zone?: string }
): DateTime {
  if (ISO_DATE_ONLY_RE.test(isoString)) {
    const year = Number(isoString.slice(0, 4))
    const month = Number(isoString.slice(5, 7))
    const day = Number(isoString.slice(8, 10))

    try {
      return DateTime.fromObject({ year, month, day }, options)
    } catch {
      // fromObject might fail on Edge browser due to Intl API issues, fallback to fromISO
      try {
        const result = DateTime.fromISO(isoString, options)
        if (result.isValid) {
          return result
        }
      } catch {
        // Both fromObject and fromISO failed, fallback to Date constructor
      }
    }

    // Final fallback: Use native Date constructor
    const jsDate = new Date(year, month - 1, day)
    if (
      jsDate.getFullYear() === year &&
      jsDate.getMonth() === month - 1 &&
      jsDate.getDate() === day
    ) {
      return DateTime.fromJSDate(jsDate, options)
    }

    const err = new Error(
      `Failed to parse date "${isoString}" (parsed: y=${year} m=${month} d=${day}). ` +
        `Invalid date or browser compatibility issue (e.g., Edge 144).`
    )
    throw err
  }

  if (options?.zone && !ISO_HAS_EXPLICIT_OFFSET_RE.test(isoString)) {
    return DateTime.fromISO(isoString, options)
  }

  const jsDate = new Date(isoString)
  return DateTime.fromJSDate(jsDate, options)
}

/**
 * Fast ISO string comparison that normalizes timezones without parsing to DateTime.
 * Uses native Date.getTime() which is much faster than parsing DateTime objects.
 *
 * @param iso1 - First ISO 8601 string
 * @param iso2 - Second ISO 8601 string
 * @returns true if both strings represent the same moment in time
 */
export function fastCompareISO(iso1: string, iso2: string): boolean {
  // if strings are identical, they're the same
  if (iso1 === iso2) return true

  const d1 = ISO_DATE_ONLY_RE.test(iso1) ? `${iso1}T00:00:00` : iso1
  const d2 = ISO_DATE_ONLY_RE.test(iso2) ? `${iso2}T00:00:00` : iso2

  return new Date(d1).getTime() === new Date(d2).getTime()
}
export function formatDate(date: DateTime): string {
  return date.toFormat('M/d')
}

/**
 * Formats a JS Date to yyyy-MM-dd string using local date components.
 * Avoids timezone conversion issues that occur with toISOString().
 */
export function formatJSDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
export function formatDT(date: DateTime): string {
  return date.toFormat('ccc M/d H:mm')
}
export function formatDTShort(date: DateTime): string {
  return date.toFormat('M/d H:mm')
}
export function formatDTMilitary(date: DateTime): string {
  return date.toFormat('M/d HH:mm')
}
export function formatDTMilitaryWDate(date: DateTime): string {
  return date.toFormat('ccc M/d HH:mm')
}
export function formatDTMilitaryWDateAndYear(date: DateTime): string {
  //  Example: Mar 5, 2024, 14:45
  return date.toFormat('LLL d, yyyy H:mm')
}

export function formatDistanceToNow(date: DateTime): string {
  const diff = date.diffNow('seconds').negate().seconds
  if (diff < 5) {
    return 'Just now'
  }
  const relative = date.toRelative({ style: 'short' })
  // Handle cases like "0 seconds ago" or "in 0 seconds"
  if (relative && relative.includes('0 sec')) {
    return 'Just now'
  }
  return relative || date.toFormat('MMM d, h:mm a')
}

export type NotificationType =
  'success' | 'warning' | 'info' | 'error' | undefined

/**
 * Gets the color class for a notification type
 */
export function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'success':
      return 'text-green-500'
    case 'error':
      return 'text-red-500'
    case 'warning':
      return 'text-yellow-500'
    case 'info':
      return 'text-blue-500'
    default:
      return 'text-gray-400'
  }
}

export function truncate(s: string, maxLen: number): string {
  const maxEffectiveLen = maxLen - 3
  if (s.length > maxEffectiveLen) {
    return `${s.substring(0, maxEffectiveLen)}...`
  } else {
    return s
  }
}

export function toTitleCase(str: string): string {
  if (!str) {
    return ''
  }
  return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
}

export function appointmentStatusColor(status: AppointmentStatus): string {
  switch (status) {
    case AppointmentStatus.Scheduled:
      return '#005ac7'
    case AppointmentStatus.Completed:
      return '#02bd2a'
    case AppointmentStatus.Missed:
      return '#ed1f26'
    case AppointmentStatus.Cancelled:
      return '#787878'
    case AppointmentStatus.Aborted:
      return '#e89700'
    case AppointmentStatus.InProgress:
      return '#edb600'
    default:
      return '#ff0000'
  }
}

export function cleanRoute(obj: object) {
  const newObj = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') {
      // @ts-expect-error - dynamic object key assignment
      newObj[k] = v
    }
  }
  return newObj
}

export function updateParam(
  router: Router,
  route: RouteLocationNormalizedLoaded,
  key: string,
  value: string | number | boolean | undefined
) {
  const newRoute = {
    ...route.query,
  }
  newRoute[key] = value === undefined ? '' : String(value)
  router.push({
    query: cleanRoute(newRoute),
  })
}

export function moveDescription(
  direction: TransactionDirection,
  loaded: boolean
): string {
  const loadedDesc = loaded ? 'Load' : 'Empty'
  const directionDesc = direction === 'inbound' ? 'In' : 'Out'
  return `${loadedDesc} ${directionDesc}`
}
let diffToSystemTime: Duration | undefined
function calculateDiffToSystemTime() {
  const now = DateTime.now()
  const systemTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone
  diffToSystemTime = now.diff(
    now.setZone(systemTimeZone, { keepLocalTime: true })
  )
  // const luxonTimeZone = now.zoneName
  // console.log(
  //   `${diffToSystemTime.toHuman()} from system timezone ${systemTimeZone} to ${luxonTimeZone}`
  // )
  return diffToSystemTime
}

export function getDiffToSystemTime(): Duration {
  if (!diffToSystemTime) {
    diffToSystemTime = calculateDiffToSystemTime()
  }
  return diffToSystemTime
}

export function toJSDate(dt: DateTime) {
  return dt.setZone('system').minus(getDiffToSystemTime()).toJSDate()
}

export const CONTAINER_RE = /^([a-z]{3})([UJZ])(\d{6})-?(\d)$/i
const CONTAINER_SEARCH_RE =
  /(?:^|(?<=[\s,|]))([a-z]{3})([UJZ])(\d{6})-?(\d)(?:(?=[\s,|])|$)/gi

/**
 * Extract canonical container numbers from free-form pasted or typed text.
 *
 * The regex matches case-insensitively, so the result has to be upper-cased
 * before it leaves here: every consumer compares it against API container
 * numbers, which are ISO-6346 canonical (always uppercase). Client-side
 * filters do that with exact `Set.has`/`includes` — the Scheduled view's
 * `matchesFilters` and the imports store's live-update filter both do — so a
 * lower-case search would match nothing at all rather than being ignored.
 * Dedupe runs after upper-casing so case variants of the same number collapse.
 */
export function parseContainerNumbers(text: string): string[] {
  const containerNumbers = [...text.matchAll(CONTAINER_SEARCH_RE)].map((m) =>
    m[0].replace(/-/g, '').toUpperCase()
  )
  return [...new Set(containerNumbers)]
}

/**
 * Split free-form pasted text into export booking numbers.
 *
 * Tokenize, uppercase and dedupe — exactly the server's normalization, and
 * deliberately **no validity filter**. Only the backend knows what a booking
 * number may be (`^[A-Z0-9]{6,20}$`, with its own message for an Excel
 * scientific-notation cell), and it reports a bad one per booking. A client
 * filter silently drops those tokens instead, so a paste of three bookings
 * turns into two rows with nothing said about the third.
 */
export function parseBookingNumbers(text: string): string[] {
  const bookingNumbers = text
    .toUpperCase()
    .split(/[\s,;|]+/)
    .filter((token) => token.length > 0)
  // Set preserves first-seen insertion order
  return [...new Set(bookingNumbers)]
}

export function shortenContainerType(
  type: ContainerType | undefined
): string | undefined {
  if (!type) {
    return type
  }
  return type.replace('Reefer', 'RF').replace(' ', '').replace('HC RF', 'RF')
}

export function confirmBox({
  title,
  message,
  confirmButtonText,
  cancelButtonText = 'Cancel',
  confirmButtonClass,
}: {
  title: string
  message: string
  confirmButtonText: string
  cancelButtonText?: string
  confirmButtonClass?: string
}): Promise<any> {
  return ElMessageBox.confirm(message, title, {
    confirmButtonText,
    confirmButtonClass,
    cancelButtonText,
  })
}

export function standardizeLicensePlate(
  licensePlateNumber: string
): string | null {
  // Replace non-alphanumeric characters (except hyphen) with empty string
  // and convert to uppercase
  const cleanedPlate = licensePlateNumber
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
    .trim()

  // Return null if the cleaned plate is empty, otherwise return the cleaned plate
  return cleanedPlate || null
}

export function calculateContainerCheckDigit(containerNumber: string): number {
  if (!containerNumber || containerNumber.length !== 10) {
    throw new Error('Invalid container number length')
  }

  // Support container numbers in lowercase
  containerNumber = containerNumber.toUpperCase()

  // Validate format: 4 letters + 6 digits (ISO 6346)
  if (!/^[A-Z]{4}\d{6}$/.test(containerNumber)) {
    throw new Error(
      'Invalid container number format (expected 4 letters + 6 digits)'
    )
  }

  const alphabet = '0123456789A BCDEFGHIJK LMNOPQRSTU VWXYZ'
  const sum = containerNumber.split('').reduce((acc, char, index) => {
    const pos = alphabet.indexOf(char)
    if (pos === -1) throw new Error('Invalid container number characters')
    return acc + pos * 2 ** index
  }, 0)

  return (sum % 11) % 10
}
